import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import AmaLogo from "@/components/app/AmaLogo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, Download, Paperclip, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicApproval() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [decided, setDecided] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/approval/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Link non valido"));
  }, [token]);

  const decide = async (decision) => {
    if (!customerName.trim()) return toast.error("Inserisci il tuo nome");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/approval/${token}`, { decision, customer_name: customerName, reason });
      setDecided(r.data.status);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSubmitting(false); }
  };

  const downloadAtt = async (a) => {
    try {
      const r = await axios.get(`${API}/public/approval/${token}/attachments/${a.id}`);
      const link = document.createElement("a");
      link.href = r.data.data_url;
      link.download = r.data.filename || r.data.display_name || "allegato";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile scaricare l'allegato");
    }
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "linear-gradient(160deg,#0a0a2e,#3a0e5e)" }}>
        <div className="ama-card p-8 max-w-md text-center">
          <XCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <div className="font-heading font-bold text-xl text-[#0a0a2e]">Link non valido</div>
          <div className="text-sm text-slate-600 mt-2">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-white bg-[#0a0a2e]">Caricamento…</div>;

  const already = data.status && data.status !== "pending";

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(160deg,#0a0a2e 0%, #3a0e5e 55%, #6f42c1 100%)" }}>
      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <div className="flex items-center justify-between text-white mb-6">
          <AmaLogo size={40} />
          <div className="text-xs text-pink-200/80 uppercase tracking-widest">{data.slogan}</div>
        </div>

        <Card className="ama-card border-0 shadow-2xl">
          <CardHeader>
            <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Autorizzazione riparazione</div>
            <CardTitle className="font-heading text-2xl md:text-3xl text-[#0a0a2e]">Pratica {data.case_number}</CardTitle>
            <div className="text-sm text-slate-600">Cliente: <b>{data.customer_company}</b></div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <Field label="Produttore" value={data.item.manufacturer_name} />
              <Field label="Prodotto" value={`${data.item.product_code} ${data.item.model}`} />
              <Field label="Seriale" value={data.item.serial_number} />
              <Field label="Numero item" value={data.item.item_number} />
            </div>

            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Problema dichiarato</div>
              <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-xl p-4 whitespace-pre-wrap text-[#0a0a2e]">{data.item.declared_problem}</div>
            </div>

            {data.diagnosis?.diagnosis_text && (
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Diagnosi del produttore</div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 whitespace-pre-wrap text-slate-700">{data.diagnosis.diagnosis_text}</div>
              </div>
            )}
            {data.diagnosis?.proposed_repair && (
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Intervento proposto</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{data.diagnosis.proposed_repair}</div>
              </div>
            )}

            <div className="bg-gradient-to-r from-fuchsia-50 to-purple-50 rounded-xl p-4 border border-fuchsia-100">
              <div className="text-xs uppercase font-semibold text-[#6f42c1]">Preventivo</div>
              <div className="text-3xl font-heading font-bold text-[#0a0a2e] mt-1">€ {Number(data.quotation.customer_price_gross || 0).toFixed(2)} <span className="text-sm text-slate-500 font-normal">IVA {data.quotation.vat_rate}% inclusa</span></div>
              <div className="text-sm text-slate-600 mt-1">Tempi stimati: {data.quotation.estimated_days} giorni · Scadenza offerta: {data.quotation.expiry_date || "-"}</div>
              {data.quotation.conditions && <div className="mt-2 text-xs text-slate-600">{data.quotation.conditions}</div>}
            </div>

            {Array.isArray(data.attachments) && data.attachments.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid="approval-attachments">
                <div className="flex items-center gap-2 text-xs uppercase font-semibold text-[#6f42c1] mb-3">
                  <Paperclip className="w-4 h-4" /> Documenti allegati al preventivo
                </div>
                <div className="space-y-2">
                  {data.attachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`approval-attachment-${a.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#0a0a2e] truncate">{a.display_name || a.filename}</div>
                        {(a.description || a.size) && (
                          <div className="text-xs text-slate-500 truncate">
                            {a.description}
                            {a.description && a.size ? " · " : ""}
                            {formatSize(a.size)}
                          </div>
                        )}
                      </div>
                      <Button size="sm" variant="outline" className="rounded-full" data-testid={`approval-attachment-download-${a.id}`} onClick={() => downloadAtt(a)}>
                        <Download className="w-3.5 h-3.5 mr-1" /> Scarica
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {decided || already ? (
              <Alert className={`${(decided || data.status) === "approved" ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                {(decided || data.status) === "approved" ? <CheckCircle2 className="w-4 h-4 text-emerald-700" /> : <XCircle className="w-4 h-4 text-rose-700" />}
                <AlertTitle>{(decided || data.status) === "approved" ? "Riparazione autorizzata" : "Riparazione non autorizzata"}</AlertTitle>
                <AlertDescription>Grazie. La sua decisione è stata registrata da AMA Sicurezza. La contatteremo per i passi successivi.</AlertDescription>
              </Alert>
            ) : (
              <>
                <div>
                  <Label>Il suo nome</Label>
                  <Input data-testid="approval-customer-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Nome e cognome" />
                </div>
                <div>
                  <Label>Motivazione (obbligatoria in caso di rifiuto)</Label>
                  <Textarea data-testid="approval-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Note per AMA Sicurezza" />
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button data-testid="approval-approve" disabled={submitting} className="ama-btn-primary rounded-full flex-1" onClick={() => decide("approved")}>
                    <ShieldCheck className="w-4 h-4 mr-2" /> Autorizzo la riparazione
                  </Button>
                  <Button data-testid="approval-reject" disabled={submitting} variant="outline" className="rounded-full flex-1" onClick={() => decide("rejected")}>
                    <XCircle className="w-4 h-4 mr-2" /> Non autorizzo
                  </Button>
                </div>
                <div className="text-xs text-slate-500">Cliccando su una delle opzioni, la sua decisione verrà registrata con data, ora e nome fornito.</div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="text-center text-pink-200/80 text-xs mt-6">© AMA Sicurezza S.r.l. · Via Leucosia 37, Salerno · info@amasicurezza.com</div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-[#0a0a2e] font-medium">{value || "-"}</div>
    </div>
  );
}
